import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {Platform, Product, noRatingLabel, sortPlatformsByCoverage} from '../../../model/product/product.model';
import {URLS} from '../../../data/navigation/navigation.data';
import {BRAND} from '../../../data/brand/brand.data';
import {Ribbon} from '@open-template-hub/card';
import {UtilService} from '../../../service/util/util.service';
import {DarkLightSettings} from '../../../data/theme/theme.data';
import {ThemeService} from '../../../service/theme/theme.service';
import {RoutingService} from '../../../service/routing/routing.service';
import {MonitorProviderService} from '../../../service/provider/monitor-provider.service';
import {posterColorSet} from '../line-color-set';

@Component({
  selector: 'app-theme-card',
  templateUrl: './theme-card.component.html',
  styleUrls: ['./theme-card.component.scss']
})
export class ThemeCardComponent implements OnInit, OnDestroy {

  @Input() product: Product = undefined;
  @Input() category;
  @Input() ribbon: Ribbon = undefined;
  @Input() screenshotMode = false;
  @Input() compact = false;
  // Tile mode: the lightest card (round logo, category, name, divider,
  // appearance) used in the mobile themes grid. No screenshots, no stats, no
  // platforms/tags - so it needs no carousel, observer, or stats fetch.
  @Input() tile = false;
  // Themes-page platform filter (a platform name, or undefined). When set the
  // card freezes on that platform's screenshot; when cleared it resumes the
  // auto-cycling carousel.
  @Input() selectedPlatform?: string;
  // Themes-page pricing filter ('Free' | 'Paid' | undefined). When set the card
  // shows only the platforms of that pricing and freezes on the first one's
  // screenshot.
  @Input() selectedPricing?: string;
  // Hide the compact card's meta row (downloads / rating / featured + platform
  // icons), used in the bundle "This bundle includes" grid where only the
  // theme's identity matters.
  @Input() hideMeta = false;
  // When false the card is display-only (no navigation / cursor), used for the
  // bundle "This bundle includes" grid.
  @Input() clickable = true;
  // Whether to show (and animate) the Downloads stat on the compact card. The
  // themes page sets this false, it never surfaced downloads, so computing the
  // count + running the counter animation there was wasted work.
  @Input() showDownloads = true;
  // When true, clicking the compact card emits `cardSelect` instead of
  // navigating to the theme page.
  @Input() selectable = false;
  // Marks the poster as a color-palette card (home + palettes page) rather than
  // a theme card. Only palette posters carry the bottom color-circle strip.
  @Input() palette = false;
  // The wall's centred card is the LCP element. When true, its background is
  // rendered as a real <img fetchpriority="high"> (instead of a CSS background)
  // so the browser can prioritise the largest-paint image, Lighthouse's
  // "fetchpriority=high should be applied" LCP check.
  @Input() priority = false;

  @Output() cardSelect = new EventEmitter<void>();

  heroLogo: string;
  darkModeActive = false;

  URLS = URLS;
  MAX_VISIBLE_PLATFORMS = 5;

  // Poster card (themes page) view state, mirroring the LINE store thumbnail:
  // the scrim/panel colour adapts to the theme's appearance via the shared
  // base sets, texts take the theme's own foregrounds, and the tag line is a
  // quiet "a · b · c" string.
  posterPanel = '#17181a';
  posterPanel0 = '#17181a00';
  /** "New" for an unrated free theme, "N/A" for premium (no solo listing). */
  readonly noRatingLabel = noRatingLabel;

  posterFg = '#ffffff';
  posterFgDim = '#9b9ba0';
  posterTags = '';

  /** The palette's 10 colours, drawn as an adjacent strip of long rectangles
   *  pinned to the poster card's bottom edge. Only palette cards carry it; empty
   *  otherwise (so the strip and its reserved space vanish). */
  get posterStrip(): string[] {
    if ( !this.palette ) {
      return [];
    }
    return ( this.product?.palette || [] ).slice( 0, 10 );
  }

  animatedDownloads = '0';
  downloadAnimationInterval?: any;
  actualDownloads = 0;
  downloadsLoaded = false;

  constructor(private router: Router, public utilService: UtilService, private themeService: ThemeService,
              private routingService: RoutingService, private monitorProviderService: MonitorProviderService) {
    this.themeService.darkLightSetting.subscribe(darkLightSetting => {

      this.heroLogo = this.getSrcWithDarkLightSetting(darkLightSetting, BRAND.logo.inlineLight, BRAND.logo.inlineDark);
      this.darkModeActive = this.themeService.selectDarkLightByCSS( false, true );

      if (DarkLightSettings.auto === darkLightSetting) {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
          this.heroLogo = this.themeService.selectDarkLightByCSS(BRAND.logo.inlineLight, BRAND.logo.inlineDark);
          this.darkModeActive = this.themeService.selectDarkLightByCSS( false, true );
        });
      }
    });
  }

  ngOnInit(): void {
    // Poster card (themes page): compute the thumbnail-style view state. The
    // old screenshot carousel is gone, the poster is a static composition,
    // so no interval, no IntersectionObserver, no per-slide repaints.
    if ( !this.compact && !this.tile ) {
      const set = posterColorSet( this.product?.appearance );
      this.posterPanel = set.bg;
      this.posterPanel0 = set.bg + '00';
      this.posterFg = this.product?.designTokens?.primaryForeground || set.fg;
      this.posterFgDim = this.product?.designTokens?.secondaryForeground || set.fgDim;
      this.posterTags = ( this.product?.tags || [] ).slice( 0, 3 ).join( ' · ' );
    }
    // Only the compact card shows the Downloads stat; skip the count + animation
    // anywhere it isn't rendered (full card, or themes page where it's hidden).
    if ( this.compact && this.showDownloads ) {
      this.startDownloadAnimation();
    }
  }

  ngOnDestroy(): void {
    if ( this.downloadAnimationInterval ) {
      this.downloadAnimationInterval();
    }
  }

  /** Kept for the themes page, which still calls it when its filters clear.
   *  The poster card has no carousel any more, so there is nothing to reset. */
  resetScreenshot(): void {
  }

  private startDownloadAnimation() {
    // Downloads exist for any product with a store listing of its own (an
    // externalId): the free themes, the bundle, and since 2026-09-14 the
    // premium themes that got a standalone paid JetBrains plugin (Ocean first).
    if ( !this.utilService.isExternalIdExist( this.product ) ) {
      this.animatedDownloads = '';
      return;
    }

    this.downloadAnimationInterval = this.utilService.startDownloadAnimation(
      () => !!( this.product?.downloads && this.product.downloads !== '0' && this.product.downloads !== '' ),
      ( value ) => this.animatedDownloads = value,
      ( finalValue ) => {
        this.downloadsLoaded = true;
        this.animatedDownloads = finalValue;
        this.downloadAnimationInterval = undefined;
      },
      () => this.utilService.parseFormattedNumber( this.product?.downloads )
    );
  }

  isSmallScreen() {
    return this.utilService.isSmallScreen();
  }

  getSrcWithDarkLightSetting(darkLightSetting: string, light: any, dark: any) {
    switch (darkLightSetting) {
      case DarkLightSettings.light: {
        return light;
      }
      case DarkLightSettings.dark: {
        return dark;
      }
      case DarkLightSettings.auto:
      default: {
        return this.themeService.selectDarkLightByCSS(light, dark);
      }
    }
  }

  protected goToTheme() {
    this.routingService.redirect( URLS.theme, {product: ( this.product.key || '' ).toLowerCase(), category: ( this.category || '' ).toLowerCase()} );
  }

  /** Compact + poster cards: emit `cardSelect` when selectable (the themes
   *  page opens its expander row), otherwise navigate to the theme page. */
  onCardClick() {
    if ( !this.clickable ) {
      return;
    }
    if ( this.selectable ) {
      this.cardSelect.emit();
    } else {
      this.goToTheme();
    }
  }

  onPlatformClick( platform: { name: string, logo: string, url: string } ) {
    this.monitorProviderService.recordClick( this.product.key, platform.name ).subscribe( {
      error: ( err ) => console.error( 'Failed to record platform click:', err )
    } );
    window.open( platform.url, '_blank' );
  }

  /** Whether a platform entry matches the active themes-page pricing filter. */
  private matchesPricing( platform: any ): boolean {
    if ( !this.selectedPricing ) {
      return true;
    }
    const isPaid = platform?.paid === true;
    return isPaid === ( this.selectedPricing === 'Paid' );
  }

  getVisiblePlatforms( limit: number = this.MAX_VISIBLE_PLATFORMS ): { name: string, logo: string, url: string }[] {
    const allPlatforms: { name: string, logo: string, url: string }[] = [];

    if ( this.product?.platforms ) {
      for ( const platform of sortPlatformsByCoverage( this.product.platforms ) ) {
        if ( !this.matchesPricing( platform ) ) {
          continue;
        }
        const collectPlatforms = (platInfo: any) => {
          if (platInfo?.supportedPlatforms && platInfo.supportedPlatforms.length > 0) {
            for (const sp of platInfo.supportedPlatforms) {
              collectPlatforms(sp);
            }
          } else if (platInfo) {
            allPlatforms.push({
              name: platInfo.name,
              logo: platInfo.logo,
              url: platform.url
            });
          }
        };
        collectPlatforms(platform.info);
      }
    }

    // Themes-page platform filter: when a platform is picked, the card shows
    // only that platform (it has also frozen the screenshot on it).
    if ( this.selectedPlatform ) {
      const sel = this.selectedPlatform.toLowerCase();
      return allPlatforms.filter( p => p.name?.toLowerCase() === sel ).slice( 0, limit );
    }

    return allPlatforms.slice( 0, limit );
  }

  getRemainingPlatformsCount( limit: number = this.MAX_VISIBLE_PLATFORMS ): number {
    // Only the selected platform is shown, so there is no "+N more".
    if ( this.selectedPlatform ) {
      return 0;
    }
    let count = 0;

    if ( this.product?.platforms ) {
      for ( const platform of this.product.platforms ) {
        if ( !this.matchesPricing( platform ) ) {
          continue;
        }
        const countPlatforms = (platInfo: any): number => {
          if (platInfo?.supportedPlatforms && platInfo.supportedPlatforms.length > 0) {
            return platInfo.supportedPlatforms.reduce((acc: number, sp: any) => acc + countPlatforms(sp), 0);
          } else if (platInfo) {
            return 1;
          }
          return 0;
        };
        count += countPlatforms(platform.info);
      }
    }

    return count > limit ? count - limit : 0;
  }
}
